/**
 * GroupPicker — the searchable portal-GROUP typeahead for the project page's
 * "Linked Group" box (astro-subject step ④).
 *
 * Structurally a sibling of AstroFamilyPicker.tsx / ClientPicker.tsx: a
 * type-to-filter input over a FLOATING listbox popover (position:absolute, its
 * own scroll) — the sanctioned overlay pattern, NOT an inline scroll box
 * embedded in the page (Rohit's no-nested-scrollbars rule).
 *
 * The difference from AstroFamilyPicker: this picks a portal **Group** (a
 * `families`-collection record, `FamilyGroup`) — the owner's model of "who the
 * project is for" — instead of the astro family directly. A Group *carries* its
 * astro link (`astroFamilyId`, stamped in step ①); the caller derives the astro
 * family from the chosen Group. Groups where `astroFamilyId` is set are badged
 * "Astro-linked"; Groups without it are pickable too, but badged so staff know
 * their charts won't resolve until the Group is reconciled to an astro family.
 *
 * It NEVER creates a Group — selection is always from the live `families`
 * directory (passed in by the panel, which already streams it via
 * `listenFamilies`). AVBP GREEN tokens only (GL-003) — never maroon/gold.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { FamilyGroup } from '../lib/familyStore';

/** One selectable portal Group, flattened for the picker + the linked display. */
export interface GroupOption {
  /** Portal `families` doc id. */
  id: string;
  /** Group display name (e.g. "Khandelwal Family"). */
  name: string;
  type: 'family' | 'organization';
  /** The astro family this Group carries, or null when not yet reconciled. */
  astroFamilyId: string | null;
  /** Convenience flag: `astroFamilyId` is a non-empty string. */
  astroLinked: boolean;
  /** Best member count to show: astro roster size if known, else portal members. */
  memberCount: number;
}

/**
 * Flatten a `FamilyGroup` to a `GroupOption`. Astro-imported Groups carry an
 * empty portal `members[]` (their roster lives in the astro app) but a cached
 * `astroMemberCount` — so prefer that for the count, falling back to the portal
 * members length for hand-built Groups.
 */
export function groupToOption(g: FamilyGroup): GroupOption {
  const astroLinked = typeof g.astroFamilyId === 'string' && g.astroFamilyId.length > 0;
  const astroCount =
    typeof g.astroMemberCount === 'number' && g.astroMemberCount > 0 ? g.astroMemberCount : 0;
  return {
    id: g.id,
    name: g.name,
    type: g.type,
    astroFamilyId: astroLinked ? (g.astroFamilyId as string) : null,
    astroLinked,
    memberCount: astroCount || g.members.length,
  };
}

/** Case-insensitive substring over the Group name (empty query = all). */
function matchesGroup(g: GroupOption, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return g.name.toLowerCase().includes(q);
}

const fieldStyle: React.CSSProperties = {
  border: '1px solid var(--rule-strong)',
  borderRadius: 'var(--r-sm)',
  padding: '8px 10px',
  fontSize: 13.5,
  fontFamily: 'inherit',
  color: 'var(--ink)',
  background: 'var(--surface)',
  width: '100%',
};

/** Small "Astro-linked" / "Not astro-linked" pill (shared with the linked display). */
export function AstroLinkBadge({ linked }: { linked: boolean }) {
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.03em',
        textTransform: 'uppercase',
        borderRadius: 'var(--r-sm)',
        padding: '1px 6px',
        whiteSpace: 'nowrap',
        color: linked ? 'var(--good-ink)' : 'var(--ink-mute)',
        background: linked ? 'var(--good-wash)' : 'transparent',
        border: `1px solid ${linked ? 'var(--good-edge)' : 'var(--rule-strong)'}`,
      }}
    >
      {linked ? 'Astro-linked' : 'Not astro-linked'}
    </span>
  );
}

export function GroupPicker({
  groups,
  loading = false,
  error = false,
  onPick,
  ariaLabel = 'Search and select a group',
  placeholder = 'Type a group name…',
  autoFocus = false,
}: {
  /** The live `families` directory, flattened by the panel. */
  groups: GroupOption[];
  loading?: boolean;
  error?: boolean;
  onPick: (group: GroupOption) => void;
  ariaLabel?: string;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Close on outside-click / Escape.
  useEffect(() => {
    if (!open) return;
    function onDoc(ev: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(ev.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(ev: KeyboardEvent) {
      if (ev.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const matches = useMemo(
    () => groups.filter((g) => matchesGroup(g, query)).slice(0, 40),
    [groups, query],
  );

  function choose(g: GroupOption) {
    onPick(g);
    setOpen(false);
    setQuery('');
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        type="text"
        value={query}
        aria-label={ariaLabel}
        placeholder={placeholder}
        autoComplete="off"
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        style={fieldStyle}
      />
      {open && (
        <div
          role="listbox"
          aria-label="Matching groups"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 50,
            maxHeight: 280,
            overflowY: 'auto',
            background: 'var(--surface)',
            border: '1px solid var(--rule-strong)',
            borderRadius: 'var(--r-sm)',
            boxShadow: 'var(--e-2)',
          }}
        >
          {loading ? (
            <div style={{ padding: '10px 12px', fontSize: 12.5, color: 'var(--ink-mute)' }}>
              Loading groups…
            </div>
          ) : error ? (
            <div style={{ padding: '10px 12px', fontSize: 12.5, color: 'var(--warn)' }}>
              Couldn’t load groups. Close and try again.
            </div>
          ) : groups.length === 0 ? (
            <div style={{ padding: '10px 12px', fontSize: 12.5, color: 'var(--ink-mute)' }}>
              No groups yet — create one in Manage → Groups, or sync from Astro.
            </div>
          ) : matches.length === 0 ? (
            <div style={{ padding: '10px 12px', fontSize: 12.5, color: 'var(--ink-mute)' }}>
              No group matches “{query}”.
            </div>
          ) : (
            matches.map((g) => (
              <button
                key={g.id}
                type="button"
                role="option"
                aria-selected={false}
                onClick={() => choose(g)}
                style={{
                  display: 'flex',
                  width: '100%',
                  gap: 10,
                  alignItems: 'center',
                  padding: '9px 12px',
                  border: 0,
                  borderBottom: '1px solid var(--rule)',
                  background: 'transparent',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: 'inherit',
                }}
              >
                <span
                  style={{
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: 'var(--ink)',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {g.name}
                </span>
                <AstroLinkBadge linked={g.astroLinked} />
                <span
                  style={{
                    marginLeft: 'auto',
                    fontSize: 11.5,
                    color: 'var(--ink-mute)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {g.memberCount} member{g.memberCount === 1 ? '' : 's'}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
